import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Bell, ShieldAlert, X, CheckCheck, Flame } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";
import { useAuth } from "../context/AuthContext";

const SEVERITY_TONES = {
  critical: "text-rose-600 dark:text-rose-400",
  high:     "text-orange-600 dark:text-orange-400",
  medium:   "text-amber-600 dark:text-amber-400",
  low:      "text-sky-600 dark:text-sky-400",
  info:     "text-muted-foreground",
};

const POLL_INTERVAL_MS = 60_000; // 1 min

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [busy, setBusy] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const isEligible = user?.role === "Admin" || user?.role === "Super Admin";

  const load = useCallback(async () => {
    if (!isEligible) return;
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch { /* silent */ }
  }, [isEligible]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isEligible) return;
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [isEligible, load]);

  // Click outside closes the panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const activeItems = (data.notifications || []).filter((n) => !n.dismissed);
  const unread = data.unread || 0;
  const watchMatchCount = activeItems.filter((n) => n.source === "watch_match").length;

  const dismiss = async (id) => {
    setBusy(true);
    try {
      await api.post("/notifications/dismiss", { id });
      await load();
    } finally { setBusy(false); }
  };

  const dismissAll = async () => {
    setBusy(true);
    try {
      await api.post("/notifications/dismiss-all");
      await load();
    } finally { setBusy(false); }
  };

  const followLink = (n) => {
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div className="relative">
      <Button
        ref={btnRef}
        variant="outline"
        size="icon"
        className="h-10 w-10 rounded-lg relative"
        aria-label={`Notifications${unread ? ` (${unread} unread${watchMatchCount ? `, ${watchMatchCount} watch-list match${watchMatchCount === 1 ? "" : "es"}` : ""})` : ""}`}
        onClick={() => setOpen((v) => !v)}
        data-testid="notifications-bell"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-rose-500 text-white text-[10px] font-bold px-1 flex items-center justify-center border-2 border-background"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
        {watchMatchCount > 0 && (
          <span
            data-testid="notifications-watchlist-dot"
            title={`${watchMatchCount} watch-list match${watchMatchCount === 1 ? "" : "es"}`}
            className="absolute -bottom-1 -right-1 min-w-[16px] h-[16px] rounded-full bg-orange-500 text-white text-[9px] font-bold px-1 flex items-center justify-center border-2 border-background animate-pulse"
          >
            {watchMatchCount > 9 ? "9+" : watchMatchCount}
          </span>
        )}
      </Button>

      {open && (
        <div
          ref={panelRef}
          data-testid="notifications-panel"
          className="absolute right-0 top-[calc(100%+8px)] z-50 w-[360px] max-h-[70vh] overflow-y-auto rounded-xl border border-border bg-popover shadow-lg"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <div className="flex items-center gap-2">
              <Bell className="w-3.5 h-3.5 text-primary" />
              <span className="text-[13px] font-semibold">Notifications</span>
              {activeItems.length > 0 && (
                <span className="text-[11px] text-muted-foreground">({activeItems.length})</span>
              )}
            </div>
            {activeItems.length > 0 && (
              <button
                data-testid="notifications-dismiss-all"
                onClick={dismissAll}
                disabled={busy}
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
              >
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          {!isEligible ? (
            <div className="p-6 text-center text-[12.5px] text-muted-foreground">
              Notifications are visible to Admins and Super Admins only.
            </div>
          ) : activeItems.length === 0 ? (
            <div className="p-6 text-center">
              <div className="w-8 h-8 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-2">
                <CheckCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div className="text-[13px] font-medium">You&apos;re all caught up</div>
              <div className="text-[11.5px] text-muted-foreground mt-1">No open vulnerability alerts.</div>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {activeItems.map((n) => {
                const isMatch = n.source === "watch_match";
                const Icon = isMatch ? Flame : ShieldAlert;
                return (
                  <li key={n.id} data-testid={`notification-${n.id}`} className="group">
                    <div className={cn(
                      "px-3 py-2.5 flex gap-2.5 transition-colors",
                      isMatch
                        ? "bg-orange-500/[0.08] hover:bg-orange-500/[0.14]"
                        : "hover:bg-muted/40",
                    )}>
                      <div className="mt-0.5">
                        <Icon className={cn("w-4 h-4", isMatch ? "text-orange-500" : (SEVERITY_TONES[n.severity] || SEVERITY_TONES.info))} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => followLink(n)}
                          className="block text-left w-full"
                        >
                          <div className="text-[13px] font-semibold leading-tight line-clamp-2">{n.title}</div>
                          {isMatch && Array.isArray(n.matches) && n.matches.length > 0 ? (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {n.matches.slice(0, 3).map((m) => (
                                <span key={m} className="inline-flex items-center rounded-full border border-orange-500/40 bg-orange-500/15 text-orange-800 dark:text-orange-200 text-[10px] font-semibold px-1.5 py-0.5">
                                  {m}
                                </span>
                              ))}
                              {n.matches.length > 3 && (
                                <span className="text-[10px] text-muted-foreground">+{n.matches.length - 3}</span>
                              )}
                              {n.source_label && (
                                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">· {n.source_label}</span>
                              )}
                            </div>
                          ) : (
                            <div className="text-[11.5px] text-muted-foreground line-clamp-2 mt-0.5">{n.message}</div>
                          )}
                          <div className="text-[10.5px] text-muted-foreground mt-1">
                            {n.link && <span className="text-primary">Open →</span>}
                          </div>
                        </button>
                      </div>
                      <button
                        onClick={() => dismiss(n.id)}
                        disabled={busy}
                        title="Dismiss"
                        className="opacity-0 group-hover:opacity-100 transition text-muted-foreground hover:text-foreground"
                        data-testid={`notification-dismiss-${n.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
